import ReturnArticlePicker from "../RetourClient/ReturnArticlePicker";
import deliveryService from "../../services/deliveryNoteService";
import customerReturnService from "../../services/customerReturnService";
import { remainingTicketLines, returnSettlement, returnWithoutTicketSettlement } from "../../helpers/customerReturnTicket";
import clientService from "../../services/clientService";
import ClientSelectModal from "./ClientSelectModal";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Label,
  FormGroup,
} from "reactstrap";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

import { customerReturnActions } from "../../sagas/customerReturnSlice";
import { productActions } from "../../sagas/productSlice";
import { clientActions } from "../../sagas/clientSlice";
import { deliveryActions } from "../../sagas/deliveryNoteSlice";

import { formatPrice } from "../../helpers/utility";
import {
  FaSearch,
  FaShoppingCart,
  FaTrashAlt,
  FaUser,
  FaReceipt,
  FaKeyboard,
  FaMoneyBillWave,
  FaCreditCard,
} from "react-icons/fa";

import "./CustomerReturnFormPOS.css";
import POSKeypadModal from "./POSKeypadModal";
import POSNumericKeypadModal from "./POSNumericKeypadModal";
import POSHeader from "./POSHeader";
import { CPMT_PRODUCTS_PAGE, CPMT_RECEPTION_PAGE, CPMT_STOCK_PAGE, PAY_PAGE, POS_PAGE, RETOUR_FORM_NEW, RETOUR_PAGE } from "../../routes/routeConstants";
import { useLocation, useNavigate } from "react-router-dom";

const CustomerReturnForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const location = useLocation();
  const ticketRequestRef = useRef(0);
  const customerReturn = location.state?.customerReturn;

  const isEditing = Boolean(customerReturn);
  const canBeUpdated =
    (isEditing && customerReturn?.statut === "BROUILLON") || !isEditing;
  const { currentShift } = useSelector(
    (state) => state.shift
  );
  const { newReturnNumber, loading } = useSelector(
    (state) => state.customerReturns
  );
  const { clients } = useSelector((state) => state.clients);
  const { list: deliverySearchResults = [], loading: deliveryLoading } = useSelector((state) => state.deliveryNotes || {});
  const { businessName, role, fullName, commercialId, pointOfSaleId } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState("RETOURS");
  const unites = useSelector((s) => s.products.unites);
  const navigate = useNavigate();

  const [numberKeypadOpen, setNumberKeypadOpen] = useState(false);
  const [numberKeypadValue, setNumberKeypadValue] = useState("");
  const [numberField, setNumberField] = useState(null);
  const [paymentModal, setPaymentModal] = useState(false);
  const [ticketNumber, setTicketNumber] = useState("");
  const [loadedTicketNumber, setLoadedTicketNumber] = useState("");
  const [ticketKeypadOpen, setTicketKeypadOpen] = useState(false);
  const [ticketSearchPerformed, setTicketSearchPerformed] = useState(false);
  const [defaultPassagerClientId, setDefaultPassagerClientId] = useState("");
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketContext, setTicketContext] = useState(null);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [returnClientId, setReturnClientId] = useState(undefined);
  const [clientAccount, setClientAccount] = useState(null);
  const [clientAccountRefresh, setClientAccountRefresh] = useState(0);
  const selectedAccountId = returnClientId ?? (customerReturn?.clientId || defaultPassagerClientId || "");

  useEffect(() => {
    let active = true;
    setClientAccount(null);
    if (selectedAccountId) {
      clientService.getClientById(selectedAccountId).then(response => {
        if (active) setClientAccount(response.data.body);
      }).catch(() => { if (active) toast.error("Impossible de vérifier le solde du client. Sélectionnez-le à nouveau."); });
    }
    return () => { active = false; };
  }, [selectedAccountId, clientAccountRefresh]);
  const submitStatusRef = useRef(null);

  const isAdmin = role === "ROLE_ADMIN";
  const getLocalISODate = () => {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return local.toISOString().split("T")[0];
  };

  // ===== INIT =====
  useEffect(() => {
    dispatch(clientActions.search({ query: "" }));
    dispatch(productActions.findAllUnites());
    if (!isEditing) dispatch(customerReturnActions.generateNumero());
  }, [dispatch, isEditing]);

  useEffect(() => {
    if (isEditing || defaultPassagerClientId) return;
    const passager = (clients || []).find((c) => {
      const label =
        c.typeClient === "PARTICULIER"
          ? `${c.nom || ""} ${c.prenom || ""}`.trim()
          : c.entreprise || `${c.nom || ""} ${c.prenom || ""}`.trim();
      return label.toUpperCase() === "PASSAGER";
    });

    if (passager?.id) {
      setDefaultPassagerClientId(passager.id);
    }
  }, [clients, defaultPassagerClientId, isEditing]);

  useEffect(() => {
    const existingNumBL = customerReturn?.numBL || "";
    if (!existingNumBL) return;

    setTicketNumber(existingNumBL);
    setLoadedTicketNumber(existingNumBL);
  }, [customerReturn?.numBL]);

  useEffect(() => {
    const reference = customerReturn?.numBL;
    if (!reference) {
      setTicketContext(null);
      return;
    }
    let active = true;
    setTicketContext(null);
    Promise.all([deliveryService.findByReference(reference), customerReturnService.linked([reference])])
      .then(([ticket, response]) => {
        if (active) setTicketContext({ reference, ticket, returns: response.data[reference] || [] });
      })
      .catch(() => { if (active) toast.error("Impossible de vérifier le paiement du ticket. Rechargez-le."); });
    return () => { active = false; };
  }, [customerReturn?.numBL]);

  // ===== HELPERS =====
  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  const recalcLineAmounts = (line) => {
    const qte = Number(line.quantiteRetournee) || 0;
    const prixTTC = Number(line.prixUnitaireTTC) || 0;
    const taux = Number(line.tauxTVA) || 0;

    const montantTTC = round2(qte * prixTTC);
    const montantHT = taux > 0 ? round2(montantTTC / (1 + taux)) : montantTTC;
    const montantTVA = round2(montantTTC - montantHT);

    return { ...line, montantHT, montantTVA, montantTTC };
  };

  const calculateTotals = (lines) => {
    let totalHT = 0;
    let totalTVA = 0;
    let totalTTC = 0;

    (lines || []).forEach((line) => {
      const qte = Number(line.quantiteRetournee) || 0;
      const ttc = Number(line.prixUnitaireTTC) || 0;
      const taux = Number(line.tauxTVA) || 0;

      const lineTotalTTC = round2(qte * ttc);
      const lineHT = taux > 0 ? round2(lineTotalTTC / (1 + taux)) : lineTotalTTC;
      const lineTVA = round2(lineTotalTTC - lineHT);

      totalTTC += lineTotalTTC;
      totalHT += lineHT;
      totalTVA += lineTVA;
    });

    return {
      montantHT: round2(totalHT),
      montantTVA: round2(totalTVA),
      montantTTC: round2(totalTTC),
    };
  };

  const getClientLabelById = (id) => {
    if (!id) return "-";
    const c = (clients || []).find((x) => String(x.id) === String(id));
    if (!c) return "-";
    return c.typeClient === "PARTICULIER"
      ? `${c.nom || ""} ${c.prenom || ""}`.trim()
      : c.entreprise || `${c.nom || ""} ${c.prenom || ""}`.trim();
  };

  // ===== VALIDATION =====
  const validationSchema = Yup.object().shape({
    returnWithoutTicket: Yup.boolean(),
    numBL: Yup.string().when("returnWithoutTicket", {
      is: true,
      then: schema => schema.notRequired(),
      otherwise: schema => schema.trim().required("Cherchez et choisissez un ticket avant de continuer."),
    }),
    dateOperation: Yup.date().required(
      t("customerReturns.form.validation.dateRequired")
    ),

    lignes: Yup.array().min(
      1,
      t("customerReturns.form.validation.itemsRequired")
    ),
  });

  const initialValues =
    isEditing && customerReturn
      ? {
        returnWithoutTicket: !customerReturn.numBL,
        numeroRetour: customerReturn.numeroRetour || "",
        dateOperation:
          customerReturn.dateOperation ||
          getLocalISODate(),
        clientId: customerReturn.clientId || "",
        numBL: customerReturn.numBL || "",
        motifRetour: customerReturn.motifRetour || "",
        modeRemboursement: customerReturn.modeRemboursement || "AVOIR",
        montantPayeCash: Number(customerReturn.montantPayeCash || 0),
        montantPayeCarte: Number(customerReturn.montantPayeCarte || 0),
        montantPaye:
          Number(customerReturn.montantPayeCash || 0) +
          Number(customerReturn.montantPayeCarte || 0),
        lignes:
          customerReturn.lignes?.map((line) => ({
            articleId: line.articleId,
            articleLibelle: line.articleNom,
            articleCode: line.articleCode,
            quantiteRetournee: line.quantiteRetournee,
            quantiteOriginale: line.quantiteOriginale,
            prixUnitaireHT: line.prixUnitaireHT,
            prixUnitaireTTC: line.prixUnitaireTTC,
            tauxTVA: line.tauxTVA,
            unite: line.unite,
          })) || [],
      }
      : {
        returnWithoutTicket: false,
        numeroRetour: newReturnNumber,
        dateOperation: getLocalISODate(),
        clientId: defaultPassagerClientId || "",
        numBL: "",
        motifRetour: "",
        modeRemboursement: "AVOIR",
        montantPayeCash: 0,
        montantPayeCarte: 0,
        montantPaye: 0,
        lignes: [],
      };

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    if (!canBeUpdated || ticketLoading || (!values.returnWithoutTicket && (!loadedTicketNumber || values.numBL !== loadedTicketNumber)) || !values.lignes.length) {
      submitStatusRef.current = null;
      setSubmitting(false);
      toast.error("Cherchez et choisissez un ticket avant de continuer.");
      return;
    }
    if (values.lignes.some((line) => !Number.isFinite(Number(line.quantiteRetournee)) || Number(line.quantiteRetournee) <= 0 || (!values.returnWithoutTicket && line.quantiteOriginale != null && Number(line.quantiteRetournee) > Number(line.quantiteOriginale)))) {
      submitStatusRef.current = null;
      setSubmitting(false);
      toast.error("La quantité dépasse les articles disponibles sur le ticket.");
      return;
    }
    const totals = calculateTotals(values.lignes);
    if (!values.returnWithoutTicket && ticketContext?.reference !== loadedTicketNumber) {
      submitStatusRef.current = null;
      setSubmitting(false);
      toast.error("Attendez la vérification du paiement du ticket.");
      return;
    }
    if (values.returnWithoutTicket && values.clientId && String(clientAccount?.id) !== String(values.clientId)) {
      submitStatusRef.current = null;
      setSubmitting(false);
      toast.error("Attendez la vérification du solde du client.");
      return;
    }
    const settlement = values.returnWithoutTicket ? returnWithoutTicketSettlement(clientAccount, totals.montantTTC)
      : returnSettlement(ticketContext.ticket, ticketContext.returns, totals.montantTTC);
    if (round2(Number(values.montantPayeCash || 0) + Number(values.montantPayeCarte || 0)) > settlement.refund) {
      submitStatusRef.current = null;
      setSubmitting(false);
      toast.error("Le remboursement dépasse le montant disponible. Confirmez à nouveau le retour.");
      return;
    }
    const forcedStatus = submitStatusRef.current;
    submitStatusRef.current = null;

    const dataToSubmit = {
      ...values,
      ...totals,
      modeRemboursement:
        forcedStatus === "VALIDE" &&
        (Number(values.montantPayeCash || 0) > 0 || Number(values.montantPayeCarte || 0) > 0)
          ? "REMBOURSEMENT"
          : values.modeRemboursement,
      montantPayeCash: Number(values.montantPayeCash || 0),
      montantPayeCarte: Number(values.montantPayeCarte || 0),
      numBL: values.returnWithoutTicket ? "" : loadedTicketNumber,
      shiftId: currentShift?.id,
      isPassager: values.clientId ? false : true,
      ...(forcedStatus ? { statut: forcedStatus } : { statut: "BROUILLON" }),
    };
    const resetAndStay = () => {
      setSubmitting(false);
      resetForm();
      setReturnClientId(undefined);
      setClientAccountRefresh(value => value + 1);
      ticketRequestRef.current++;
      setTicketNumber("");
      setLoadedTicketNumber("");
      setTicketSearchPerformed(false);
      setPaymentModal(false);
      if (!isEditing) {
        dispatch(customerReturnActions.generateNumero());
      }
    };

    if (isEditing) {
      dispatch(
        customerReturnActions.update({
          id: customerReturn?.id,
          data: dataToSubmit,
          onSuccess: () => {
            Swal.fire({
              icon: "success",
              title: t("common.success"),
              text: t("customerReturns.messages.updateSuccess"),
            });
            resetAndStay();
          },
          onError: (error) => {
            Swal.fire({
              icon: "error",
              title: t("common.error"),
              text: error?.response?.data?.message || error?.message || t("customerReturns.messages.saveError"),
            });
            setSubmitting(false);
            setPaymentModal(false);
          },
        })
      );
    } else {
      dispatch(
        customerReturnActions.create({
          data: dataToSubmit,
          onSuccess: () => {
            Swal.fire({
              icon: "success",
              title: t("common.success"),
              text: t("customerReturns.messages.updateSuccess"),
            });
            resetAndStay();
          },
          onError: (error) => {
            Swal.fire({
              icon: "error",
              title: t("common.error"),
              text: error?.response?.data?.message || error?.message || t("customerReturns.messages.saveError"),
            });
            setSubmitting(false);
            setPaymentModal(false);
          },
        })
      );
    }
  };

  if (loading && isEditing) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="pos-form-page">
      <POSHeader
        businessName={businessName || "MATJARPOS"}
        commercialId={commercialId}
        pointOfSaleId={pointOfSaleId}
        fullName={fullName}
        activeTab={activeTab}
        onChangeTab={(tab) => {
          setActiveTab(tab);
          if (tab === "VENTES") navigate(POS_PAGE);
          if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
          if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
          if (tab === "AVANCE") navigate(PAY_PAGE);
          if (tab === "RETOURS") navigate(RETOUR_FORM_NEW);
          if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
        }}
      />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, isSubmitting, setFieldValue, submitForm }) => {
          const totals = calculateTotals(values.lignes);
          const lineCount = values.lignes?.length || 0;
          const accountReady = !values.clientId || String(clientAccount?.id) === String(values.clientId);
          const canSave = canBeUpdated && !ticketLoading && (values.returnWithoutTicket ? accountReady : (Boolean(loadedTicketNumber) && values.numBL === loadedTicketNumber && ticketContext?.reference === loadedTicketNumber)) && lineCount > 0;
          const settlement = values.returnWithoutTicket ? returnWithoutTicketSettlement(values.clientId && accountReady ? clientAccount : null, totals.montantTTC)
            : ticketContext?.reference === loadedTicketNumber ? returnSettlement(ticketContext.ticket, ticketContext.returns, totals.montantTTC)
              : { refund: 0, debtReduction: 0 };
          const paidCash = Number(values.montantPayeCash || 0);
          const paidCard = Number(values.montantPayeCarte || 0);
          const paidTotal = paidCash + paidCard;

          const selectReturnPayment = (mode) => {
            const total = settlement.refund;
            setFieldValue("modeRemboursement", total > 0 ? "REMBOURSEMENT" : "AVOIR");
            if (mode === "CARTE_BANCAIRE") {
              setFieldValue("montantPayeCash", 0);
              setFieldValue("montantPayeCarte", total);
              setFieldValue("montantPaye", total);
              return;
            }
            setFieldValue("montantPayeCash", total);
            setFieldValue("montantPayeCarte", 0);
            setFieldValue("montantPaye", total);
          };

          const openReturnCheckout = () => {
            if (!canSave) return;
            selectReturnPayment(paidCard > 0 ? "CARTE_BANCAIRE" : "ESPECES");
            setPaymentModal(true);
          };

          const confirmReturnCheckout = () => {
            if (round2(paidTotal) !== settlement.refund) {
              Swal.fire({
                icon: "warning",
                title: t("common.information"),
                text: "Vérifiez le montant à rembourser.",
              });
              return;
            }
            submitStatusRef.current = "VALIDE";
            setPaymentModal(false);
            submitForm();
          };

          const mapDeliveryLineToReturnLine = (line) =>
            recalcLineAmounts({
              article: line.articleId,
              articleId: line.articleId,
              articleLibelle: line.articleNom || line.articleLibelle || "-",
              articleCode: line.articleCode || "",
              quantiteRetournee: Number(line.quantiteLivree ?? line.quantite ?? line.quantiteRetournee ?? 1) || 1,
              quantiteOriginale: Number(line.quantiteLivree ?? line.quantite ?? line.quantiteOriginale ?? 1) || 1,
              prixUnitaireHT: Number(line.prixUnitaireHT ?? 0),
              prixUnitaireTTC: Number(line.prixUnitaireTTC ?? 0),
              tauxTVA: Number(line.tauxTVA ?? 0.2),
              unite: line.unite || "",
            });

          const handleTicketNumberChange = (nextValue) => {
            const normalizedNext = (nextValue || "").trim();
            const normalizedLoaded = (loadedTicketNumber || "").trim();
            const hasChangedLoadedBL =
              normalizedLoaded &&
              normalizedNext.toUpperCase() !== normalizedLoaded.toUpperCase();

            ticketRequestRef.current++;
            setTicketLoading(false);
            setTicketNumber(nextValue);
            setTicketSearchPerformed(false);

            if (hasChangedLoadedBL) {
              setFieldValue("lignes", []);
              setFieldValue("numBL", "");
              setLoadedTicketNumber("");
            }
          };

          const getDeliveryStatus = (deliveryRecord) =>
            deliveryRecord?.statut || deliveryRecord?.status || "";

          const isReturnableDelivery = (deliveryRecord) =>
            getDeliveryStatus(deliveryRecord) === "LIVRE";

          const applyDeliveryToReturn = async (deliveryRecord) => {
            if (!canBeUpdated || ticketLoading) return;
            const request = ++ticketRequestRef.current;
            const bl = deliveryRecord?.body || deliveryRecord?.data?.body || deliveryRecord?.data || deliveryRecord;
            const deliveryStatus = getDeliveryStatus(bl);

            if (!isReturnableDelivery(bl)) {
              Swal.fire({
                icon: "warning",
                title: t("common.information"),
                text: `Le BL ${bl?.numero || ""} est au statut ${deliveryStatus || "-"}. Seuls les BL LIVRE peuvent être utilisés pour un retour.`,
              });
              return;
            }

            const lignes = Array.isArray(bl?.lignes) ? bl.lignes : [];

            if (!lignes.length) {
              Swal.fire({
                icon: "info",
                title: t("common.information"),
                text: "Aucune ligne trouvée pour ce ticket.",
              });
              return;
            }

            let remaining;
            let context;
            setTicketLoading(true);
            try {
              const response = await customerReturnService.linked([bl.numero]);
              const fullTicket = await deliveryService.findByReference(bl.numero);
              context = { reference: bl.numero, ticket: fullTicket, returns: response.data[bl.numero] || [] };
              remaining = remainingTicketLines(fullTicket, context.returns);
            } catch (error) {
              toast.error("Impossible de verifier les retours de ce ticket. Reessayez.");
              return;
            } finally {
              if (request === ticketRequestRef.current) setTicketLoading(false);
            }
            if (request !== ticketRequestRef.current) return;
            if (!remaining.length) {
              toast.info("Tous les articles de ce ticket ont deja ete retournes.");
              return;
            }
            const mappedLines = remaining.map(mapDeliveryLineToReturnLine);
            setTicketContext(context);
            setFieldValue("montantPayeCash", 0);
            setFieldValue("montantPayeCarte", 0);
            setFieldValue("montantPaye", 0);
            setFieldValue("modeRemboursement", "AVOIR");
            setFieldValue("lignes", mappedLines);
            setFieldValue("numBL", bl?.numero || ticketNumber);

            setFieldValue("clientId", bl.clientId || defaultPassagerClientId || "");

            setLoadedTicketNumber(bl?.numero || ticketNumber);
            setTicketNumber(bl?.numero || ticketNumber);
            setTicketSearchPerformed(false);
            toast.success("Ticket chargé avec succès.");
          };

          const handleSearchTickets = () => {
            const reference = (ticketNumber || "").trim();
            if (!reference) {
              Swal.fire({
                icon: "warning",
                title: t("sale.error"),
                text: "Veuillez saisir un numéro de ticket/BL.",
              });
              return;
            }

            setTicketSearchPerformed(true);
            dispatch(
              deliveryActions.listComptoire({
                query: reference,
                onError: (error) => {
                  Swal.fire({
                    icon: "error",
                    title: t("common.error"),
                    text: "Impossible de rechercher les tickets.",
                  });
                },
              })
            );
          };

          return (
            <Form className="pos-form-root">


              <Row className="g-2 mt-0 pos-workspace">
                <Col xl={7} lg={7} className="pos-col-stretch">
                  <Card className="pos-card pos-card-fill">
                    <CardBody className="pos-card-body pos-return-ticket-panel">
                      <div className="d-flex gap-2 mb-3">
                        {[false, true].map(withoutTicket => <Button key={String(withoutTicket)} type="button"
                          color={values.returnWithoutTicket === withoutTicket ? "primary" : "light"}
                          aria-pressed={values.returnWithoutTicket === withoutTicket} disabled={!canBeUpdated}
                          onClick={() => {
                            if (values.returnWithoutTicket === withoutTicket) return;
                            ticketRequestRef.current++;
                            setTicketLoading(false);
                            setTicketNumber("");
                            setLoadedTicketNumber("");
                            setTicketSearchPerformed(false);
                            setFieldValue("returnWithoutTicket", withoutTicket);
                            setFieldValue("numBL", "");
                            setFieldValue("lignes", []);
                            setFieldValue("clientId", defaultPassagerClientId || "");
                            setReturnClientId(defaultPassagerClientId || "");
                            setFieldValue("montantPayeCash", 0);
                            setFieldValue("montantPayeCarte", 0);
                          }}>{withoutTicket ? "Retour sans ticket" : "Retour avec ticket"}</Button>)}
                      </div>
                      {values.returnWithoutTicket ? <ReturnArticlePicker lines={values.lignes} disabled={!canBeUpdated}
                        onAdd={line => setFieldValue("lignes", [...values.lignes, recalcLineAmounts(line)])} /> : <>
                      <h1 className="h4 fw-bold"><FaReceipt className="me-2" />Retour avec ticket</h1>
                      <p className="text-muted">Gardez le ticket du client devant vous.</p>
                      <h2 className="h5 fw-bold mb-3">1. Trouver le ticket</h2>
                        <div className="mb-3">
                          <Label for="return-ticket-number" className="pos-label mb-2">
                            <FaReceipt className="me-2" />
                            Numéro du ticket
                          </Label>
                          <div className="d-flex gap-2">
	                            <Input
	                              id="return-ticket-number"
	                              type="text"
	                              value={ticketNumber}
	                              readOnly
	                              inputMode="none"
	                              onClick={() => {
	                                if (!canBeUpdated || deliveryLoading) return;
	                                setTicketKeypadOpen(true);
	                              }}
	                              onKeyDown={(e) => {
	                                if (e.key === "Enter") {
	                                  e.preventDefault();
	                                  handleSearchTickets();
	                                }
	                              }}
                              placeholder="Ex: BL-000123"
                              className="pos-input"
                              disabled={!canBeUpdated || deliveryLoading || ticketLoading}
                            />
                            <Button
                              type="button"
                              color="light"
                              onClick={() => setTicketKeypadOpen(true)}
                              disabled={!canBeUpdated || deliveryLoading || ticketLoading}
                            >
                              <FaKeyboard className="me-2" />Saisir
                            </Button>
	                            <Button
	                              type="button"
	                              color="primary"
	                              onClick={handleSearchTickets}
	                              disabled={!canBeUpdated || deliveryLoading || ticketLoading}
	                            >
	                              {deliveryLoading ? <Spinner size="sm" /> : <FaSearch />} <span className="ms-2">Chercher</span>
	                            </Button>
	                          </div>
	                          {loadedTicketNumber ? (
	                            <small className="text-success d-block mt-2">
	                              Ticket chargé: {loadedTicketNumber}
	                            </small>
	                          ) : null}
                            <div className="mt-3">
                              {deliveryLoading ? (
                                <div className="text-center py-3">
                                  <Spinner size="sm" color="primary" />
                                </div>
                              ) : ticketSearchPerformed ? (
                                deliverySearchResults.length === 0 ? (
                                  <div className="text-muted small">
                                    Aucun ticket trouvé pour cette recherche.
                                  </div>
                                ) : (
                                  <div className="d-grid gap-2">
                                    {deliverySearchResults.map((bl) => {
                                      const deliveryStatus = getDeliveryStatus(bl);
                                      const canUseForReturn = isReturnableDelivery(bl);

                                      return (
                                      <Card
                                        key={bl.id}
                                        className={`border shadow-sm ${canUseForReturn ? "" : "opacity-75"}`}
                                        style={{ cursor: canUseForReturn ? "pointer" : "not-allowed" }}
                                        onClick={() => {
                                          if (!canUseForReturn) return;
                                          applyDeliveryToReturn(bl);
                                        }}
                                      >
                                        <CardBody className="py-3">
                                          <div className="d-flex justify-content-between align-items-start gap-3">
                                            <div>
                                              <div className="fw-bold">{bl.numero || "-"}</div>
                                              <div className="text-muted small">
                                                {bl.clientNom || "Client non renseigné"}
                                              </div>
                                              <div className="small mt-1">
                                                <Badge color={canUseForReturn ? "success" : "secondary"}>
                                                  {deliveryStatus || "-"}
                                                </Badge>
                                                {!canUseForReturn ? (
                                                  <span className="text-danger ms-2">Non utilisable pour retour</span>
                                                ) : null}
                                              </div>
                                            </div>
                                            <div className="text-end">
                                              <div className="fw-bold text-primary">
                                                {formatPrice(bl.montantTotalTTC || 0)}
                                              </div>
                                              <Button
                                                type="button"
                                                size="sm"
                                                color={canUseForReturn ? "primary" : "secondary"}
                                                className="mt-2"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (!canUseForReturn) return;
                                                  applyDeliveryToReturn(bl);
                                                }}
                                                disabled={!canUseForReturn || !canBeUpdated || ticketLoading}
                                              >
                                                {ticketLoading ? "Chargement…" : canUseForReturn ? "Utiliser ce ticket" : "Indisponible"}
                                              </Button>
                                            </div>
                                          </div>
                                        </CardBody>
                                      </Card>
                                      );
                                    })}
                                  </div>
                                )
                              ) : null}
                            </div>
	                        </div>
                      </>}
                      {ticketLoading && <p role="status"><Spinner size="sm" className="me-2" />Chargement des articles…</p>}
                      {errors.numBL && touched.numBL && <p className="text-danger" role="alert">{errors.numBL}</p>}
                      <div className="bg-light rounded-3 p-3 mt-3">
                        <p className="fw-bold mb-2">2. Choisir les articles à retourner</p>
                        <p className="mb-2">Retirez les articles que le client garde avec la corbeille. Touchez la quantité pour la changer.</p>
                        <p className="fw-bold mb-0">3. Appuyer sur « Confirmer le retour »</p>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={5} lg={5} className="pos-col-stretch pos-right pos-cart-pane">
                  <Card className="pos-card shadow-sm rounded-4 border-0 mb-3">
                    <CardBody className="pos-card-body">
                      <div className="fw-bold"><FaUser className="me-2" />{values.returnWithoutTicket ? "Client" : "Client du ticket"}</div>
                      {values.returnWithoutTicket ? <>
                        <Button type="button" color="light" className="w-100 text-start border mt-2 py-2 fw-bold"
                          disabled={!canBeUpdated || isSubmitting} onClick={() => setClientModalOpen(true)}>
                          <FaUser className="me-2" />{values.clientId ? getClientLabelById(values.clientId) : "Client passager"} — Choisir
                        </Button>
                        {!accountReady ? <p className="mt-2 mb-0 text-muted" role="status">Vérification du solde…</p>
                          : <p className="small text-muted mt-2 mb-0">Le retour réduit d’abord le crédit impayé du client. Seul le reste est remboursé.</p>}
                        {accountReady && clientAccount && getClientLabelById(values.clientId).trim().toUpperCase() !== "PASSAGER" && <p className="fw-bold mt-2 mb-0">Reste impayé : {formatPrice(Math.max(0, Number(clientAccount.totalDebit || 0) - Number(clientAccount.totalCredit || 0)))}</p>}
                      </> : <div className="mt-1">{values.clientId ? getClientLabelById(values.clientId) : "Client passager"}</div>}
                    </CardBody>
                  </Card>

                  <Card className="pos-card pos-card-fill pos-cart-card shadow-sm rounded-4 border-0">
                    <CardHeader className="bg-light fw-bold d-flex justify-content-between align-items-center flex-shrink-0">
                        <span>
                          <FaShoppingCart className="me-2" />
                          2. Articles à retourner
                        </span>
                        <div className="d-flex align-items-center gap-2">
                          {lineCount > 0 && <Badge color="primary">{lineCount}</Badge>}
                        </div>
                    </CardHeader>

                    <CardBody className="p-0 d-flex flex-column h-100">
                      <FieldArray name="lignes">
                        {({ remove }) => (
                          <div className="pos-return-lines pos-cart-items">
                            {values.lignes.length === 0 ? (
                              <div className="text-center text-muted py-4">
                                {values.returnWithoutTicket ? "Choisissez les articles dans la liste." : loadedTicketNumber ? "Aucun article sélectionné. Choisissez à nouveau le ticket pour recommencer." : "Cherchez un ticket pour voir ses articles."}
                              </div>
                            ) : (
                              values.lignes.map((line, index) => (
                                <div key={index} className="pos-return-line pos-cart-line">
                                  <div className="pos-sale-line-layout pos-return-compact-row">
                                    <div className="pos-return-compact-info">
                                      <strong className="pos-return-compact-name" title={line.articleLibelle || "Article"}>{line.articleLibelle || "Article"}</strong>
                                      <span className="pos-return-compact-barcode text-muted" title="Code-barres">{line.articleCode || "—"}</span>
                                      <span className="pos-return-compact-price text-muted" title="Prix unitaire">
                                        {formatPrice(line.prixUnitaireTTC || 0, false)} DH
                                        {" / "}{unites?.find((u) => u.code === line.unite)?.libelle || line.unite || "-"}
                                      </span>
                                    </div>
                                        <Input
                                          aria-label={`Quantité à retourner : ${line.articleLibelle || "Article"}`}
                                          title={`Quantité à retourner${line.quantiteOriginale != null ? ` (maximum : ${line.quantiteOriginale})` : ""}`}
                                          value={line.quantiteRetournee ?? 1}
                                          readOnly
                                          disabled={!canBeUpdated}
                                          className="pos-cart-input pos-return-compact-quantity"
                                          onClick={() => {
                                            if (!canBeUpdated) return;
                                            setNumberField({ type: "QTE", index });
                                            setNumberKeypadValue(String(line.quantiteRetournee ?? 1));
                                            setNumberKeypadOpen(true);
                                          }}
                                        />
                                      <strong className="pos-return-compact-total" title="Total de l'article">{formatPrice(line.montantTTC || 0, false)}</strong>
                                      <Button
                                        type="button"
                                        color="danger"
                                        size="sm"
                                        outline
                                        disabled={!canBeUpdated}
                                        className="pos-return-compact-delete"
                                        title="Retirer cet article"
                                        aria-label={`Retirer ${line.articleLibelle || "cet article"}`}
                                        onClick={() => remove(index)}
                                      >
                                        <FaTrashAlt aria-hidden="true" />
                                      </Button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </FieldArray>

                      {errors.lignes && typeof errors.lignes === "string" ? (
                        <div className="text-danger mt-2 fw-bold">{errors.lignes}</div>
                      ) : null}

                      <div className="pos-cart-footer">
                        <div className="d-flex justify-content-between fw-bold fs-5">
                          <span>Montant à rembourser</span>
                          <strong>{values.returnWithoutTicket && !accountReady ? "…" : formatPrice(settlement.refund)}</strong>
                        </div>
                        {settlement.debtReduction > 0 && <p className="text-primary mb-0 mt-2">Crédit client réduit de {formatPrice(settlement.debtReduction)}.</p>}

                      <div className="d-flex gap-2 mt-3 flex-wrap">
                        <Button
                          color="success"
                          size="lg"
                          className="flex-fill fw-bold"
                          type="button"
                          disabled={isSubmitting || !canSave}
                          onClick={openReturnCheckout}
                        >
                          {settlement.refund > 0 ? "Rembourser le client" : "Confirmer le retour"}
                        </Button>
                        <Button
                          color="primary"
                          size="lg"
                          className="flex-fill fw-bold"
                          type="submit"
                          disabled={isSubmitting || !canSave}
                        >
                          {isSubmitting ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              {t("common.loading")}
                            </>
                          ) : (
                            "Garder en brouillon"
                          )}
                        </Button>
                        {!isAdmin && (
                          <Button
                            color="info"
                            outline
                            size="lg"
                            className="fw-bold"
                            onClick={() => navigate(RETOUR_PAGE)}
                          >
                            {t("common.list", "Liste")}
                          </Button>
                        )}
                      </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              <Modal isOpen={paymentModal} toggle={() => setPaymentModal(false)} size="lg" centered>
                <ModalHeader toggle={() => setPaymentModal(false)}>
                  {settlement.refund > 0 ? "Confirmer le remboursement" : "Confirmer le retour"}
                </ModalHeader>
                <ModalBody>
                  <Card className="border-0 shadow-sm bg-light mb-3">
                    <CardBody>
                      <Row className="g-3 align-items-center">
                        <Col md={6}>
                          <div className="text-muted fw-bold text-uppercase small mb-2">
                            <FaMoneyBillWave className="me-2 text-success" />
                            Montant à rembourser
                          </div>
                          <div className="fs-3 fw-bold text-success">
                            {formatPrice(settlement.refund)}
                          </div>
                        </Col>
                        {settlement.refund > 0 && <Col md={6}>
                          <div className="text-muted fw-bold text-uppercase small mb-2">
                            <FaCreditCard className="me-2 text-primary" />
                            Mode de paiement
                          </div>
                          <div className="d-flex flex-wrap gap-2">
                            <Button
                              type="button"
                              color={paidCash > 0 ? "primary" : "secondary"}
                              className="d-flex align-items-center px-4 py-2 fw-bold"
                              onClick={() => selectReturnPayment("ESPECES")}
                            >
                              <FaMoneyBillWave className="me-2" />
                              Espèces
                            </Button>
                            <Button
                              type="button"
                              color={paidCard > 0 ? "primary" : "secondary"}
                              className="d-flex align-items-center px-4 py-2 fw-bold"
                              onClick={() => selectReturnPayment("CARTE_BANCAIRE")}
                            >
                              <FaCreditCard className="me-2" />
                              Carte
                            </Button>
                          </div>
                        </Col>}
                      </Row>
                      {settlement.debtReduction > 0 && <p className="mt-3 mb-0 fw-bold text-primary">Crédit client réduit de {formatPrice(settlement.debtReduction)}. {settlement.refund === 0 && "Aucun argent à rendre au client."}</p>}
                      {settlement.refund > 0 && <Row className="g-3 mt-2">
                        <Col md={4}>
                          <FormGroup className="mb-0">
                            <Label className="fw-bold text-muted">
                              <FaMoneyBillWave className="me-2 text-success" />
                              Espèces
                            </Label>
                            <Input
                              type="number"
                              readOnly
                              value={paidCash}
                              className="bg-white py-2 fs-5 fw-bold"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup className="mb-0">
                            <Label className="fw-bold text-muted">
                              <FaCreditCard className="me-2 text-primary" />
                              Carte
                            </Label>
                            <Input
                              type="number"
                              readOnly
                              value={paidCard}
                              className="bg-white py-2 fs-5 fw-bold"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup className="mb-0">
                            <Label className="fw-bold text-muted">Total remboursé</Label>
                            <Input
                              type="number"
                              readOnly
                              value={paidTotal}
                              className="bg-white py-2 fs-5 fw-bold text-success"
                            />
                          </FormGroup>
                        </Col>
                      </Row>}
                    </CardBody>
                  </Card>
                </ModalBody>
                <ModalFooter className="d-flex justify-content-end">
                  <Button
                    color="primary"
                    size="lg"
                    className="px-4"
                    disabled={isSubmitting || !canSave || round2(paidTotal) !== settlement.refund}
                    onClick={confirmReturnCheckout}
                  >
                    {isSubmitting && <Spinner size="sm" className="me-2" />}
                    {t("payment.confirm", "Confirmer")}
                  </Button>
                  <Button
                    color="secondary"
                    size="lg"
                    className="px-4 ms-2"
                    disabled={isSubmitting}
                    onClick={() => setPaymentModal(false)}
                  >
                    {t("payment.cancel", "Annuler")}
                  </Button>
                </ModalFooter>
              </Modal>
	
              <ClientSelectModal isOpen={clientModalOpen} toggle={() => setClientModalOpen(false)}
                clients={(clients || []).map(client => ({ ...client, value: client.id, label: getClientLabelById(client.id) }))}
                selectedClient={values.clientId ? { value: values.clientId, label: getClientLabelById(values.clientId) } : null}
                onSelect={client => {
                  setReturnClientId(client?.value || "");
                  setClientAccount(null);
                  setClientAccountRefresh(value => value + 1);
                  setFieldValue("clientId", client?.value || "");
                  setFieldValue("montantPayeCash", 0);
                  setFieldValue("montantPayeCarte", 0);
                  setFieldValue("montantPaye", 0);
                  setFieldValue("modeRemboursement", "AVOIR");
                  setPaymentModal(false);
                }} />
              <POSKeypadModal
                isOpen={ticketKeypadOpen}
                toggle={() => setTicketKeypadOpen(false)}
                value={ticketNumber}
                onChange={handleTicketNumberChange}
                onSearch={() => {
                  handleSearchTickets();
                  setTicketKeypadOpen(false);
                }}
                title="Numéro Ticket / BL"
              />

              <POSNumericKeypadModal
                isOpen={numberKeypadOpen}
                toggle={() => setNumberKeypadOpen(false)}
                value={numberKeypadValue}
                onChange={setNumberKeypadValue}
                onConfirm={(val) => {
                  const num = Number(val) || 0;

                  if (numberField?.type === "QTE") {
                    if (!Number.isFinite(num) || num <= 0 || (!values.returnWithoutTicket && num > Number(values.lignes[numberField.index].quantiteOriginale))) {
                      toast.error("Quantite invalide pour ce ticket.");
                      return;
                    }
                    const updatedLine = recalcLineAmounts({
                      ...values.lignes[numberField.index],
                      quantiteRetournee: num,
                    });
                    setFieldValue(`lignes.${numberField.index}`, updatedLine);
                  }

                  setNumberKeypadOpen(false);
                }}
                title="Saisie numérique"
              />
            </Form>
          );
        }}

      </Formik>
    </Container>
  );
};

export default CustomerReturnForm;



